<template>
  <rest-page ref="marketing-rest-page" :fetch-endpoint="fetchEndpoint">
    <el-row class="addressability-container" style="" v-if="model">
      <el-col class="addressability-left-section" :span="12">
        <page-section :title="$t('Profiles_Channels_Title_Channels')">
          <channels-menu :items="channelItems" />
        </page-section>
      </el-col>

      <el-col class="addressability-right-section" :span="12">
        <page-section :title="$t('Profiles_Channels_Title_MarketingOptIn')">
          <channels-menu :items="marketingOptItems">
            <template slot="actions" slot-scope="{ prop, channel }">
              <el-dropdown
                trigger="click"
                :key="prop"
                v-if="!readonly"
                :data-qa="`marketing-option-menu-${channel}`"
              >
                <i class="el-icon-more"></i>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-if="model[prop] !== 1"
                    icon="el-icon-success"
                    @click.native="updateSubscription(prop, 'subscribe')"
                    :data-qa="`marketing-option-subscribe-${channel}`"
                  >
                    {{ $t('Profiles_Channels_Action_Subscribe') }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="model[prop] !== 0"
                    icon="el-icon-error"
                    @click.native="updateSubscription(prop, 'unsubscribe')"
                    :data-qa="`marketing-option-unsubscribe-${channel}`"
                  >
                    {{ $t('Profiles_Channels_Action_Unsubscribe') }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="subscriptionTrackingEnabled"
                    icon="el-icon-document"
                    @click.native="showSubscriptionLogs(channel)"
                    :data-qa="`marketing-option-view-log-${channel}`"
                  >
                    {{ $t('Profiles_Channels_Action_Log') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </channels-menu>

          <subscription-logs-popup
            ref="subscription-logs"
            :profile-id="$route.query.profile_id"
          />
        </page-section>
      </el-col>
    </el-row>

    <page-section
      v-if="model && $hasFeature('subscriptionPreferences')"
      :title="$t('Profiles_Channels_Title_ConsentPreferences')"
      class="m-top-20"
    >
      <consent-preferences :profile-id="$route.query.profile_id" />
    </page-section>

    <page-section
      v-if="model && $hasFeature('subscriptionPreferences')"
      :title="$t('Profiles_Channels_Title_DataConsentPreferences')"
      class="m-top-40"
    >
      <consent-preferences
        :profile-id="$route.query.profile_id"
        :with-channels="false"
      />
    </page-section>

    <page-section
      v-if="model && $can('Device::Read')"
      :title="$t('Profiles_Channels_Title_PushSubscriptions')"
      class="m-top-40"
    >
      <profile-push-subscriptions :profile-id="$route.query.profile_id" />
    </page-section>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import ChannelsMenu from '@/modules/profiles/components/ChannelsMenu'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import PageSection from '@/common/components/page/PageSection'
import ProfilePushSubscriptions from '@/modules/profiles/components/ProfilePushSubscriptions'
import SubscriptionLogsPopup from '@/modules/profiles/components/SubscriptionLogsPopup'
import { $hasFeature } from '@/core/utils/VueAcl'
import ConsentPreferences from '@/modules/profiles/components/ConsentPreferences'
import { subscriptionStatusKeys } from '@/modules/profiles/enum/SubscriptionStatusEnum'

export default {
  name: 'ProfileChannelsPage',

  components: {
    ConsentPreferences,
    SubscriptionLogsPopup,
    ProfilePushSubscriptions,
    PageSection,
    ChannelsMenu,
    RestPage
  },

  data() {
    return {
      model: {},
      subscriptionPreferencesStatus: [],
      subscriptionPreferences: []
    }
  },

  computed: {
    readonly() {
      return !this.$can('Profile::Update')
    },

    marketingOptItems() {
      return [
        {
          title: this.$t('Profiles_Channels_NavItem_EmailMarketing'),
          icon: this.getMarketingStatusIcon(this.model.email_subscription),
          prop: 'email_subscription',
          channel: 'email',
          actions: true,
          status: this.getMargetingStatusLabel(
            this.model.email_subscription,
            'email'
          )
        },
        {
          title: this.$t('Profiles_Channels_NavItem_SmsMarketing'),
          icon: this.getMarketingStatusIcon(this.model.sms_subscription),
          prop: 'sms_subscription',
          channel: 'sms',
          actions: true,
          status: this.getMargetingStatusLabel(
            this.model.sms_subscription,
            'sms'
          )
        },
        {
          title: this.$t('Profiles_Channels_NavItem_WhatsappMarketing'),
          icon: this.getMarketingStatusIcon(this.model.whatsapp_subscription),
          prop: 'whatsapp_subscription',
          channel: 'whatsapp',
          actions: true,
          status: this.getMargetingStatusLabel(
            this.model.whatsapp_subscription,
            'whatsapp'
          )
        }
      ]
    },

    channelItems() {
      return [
        {
          title: this.$t('Profiles_Channels_NavItem_EmailAddress'),
          icon: this.getChannelStatusIcon(
            this.model.email_addressable,
            this.model.email
          ),
          status: this.getChannelStatusLabel(
            this.model.email_addressable,
            this.model.email
          )
        },
        {
          title: this.$t('Profiles_Channels_NavItem_MobileNumber'),
          icon: this.getChannelStatusIcon(
            this.model.sms_addressable,
            this.model.mobile_number
          ),
          status: this.getChannelStatusLabel(
            this.model.sms_addressable,
            this.model.mobile_number
          )
        },
        {
          title: this.$t('Profiles_Channels_NavItem_WhatsappNumber'),
          icon: this.getChannelStatusIcon(
            this.model.whatsapp_addressable,
            this.model.whatsapp_number
          ),
          status: this.getChannelStatusLabel(
            this.model.whatsapp_addressable,
            this.model.whatsapp_number
          )
        }
      ]
    },

    fetchEndpoint() {
      return [
        projectEndpoint({
          method: 'get',
          url: `user-profiles/${this.$route.query.profile_id}`,
          handler: data => {
            this.model = data
          }
        }),

        projectEndpoint({
          method: 'get',
          url: `subscription-preferences`,
          handler: data => {
            this.subscriptionPreferences = data
          }
        })
      ]
    },

    saveEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: `user-profiles/${this.$route.query.profile_id}`
      })
    },

    subscriptionTrackingEnabled() {
      return $hasFeature('subscriptionTracking')
    }
  },

  methods: {
    updateSubscription(prop, option) {
      switch (option) {
        case 'subscribe':
          this.updateModel(prop, 1)
          break
        case 'unsubscribe':
          this.updateModel(prop, 0)
          break
      }
    },

    updateModel(prop, newValue) {
      this.$refs['marketing-rest-page'].loading = true
      let oldValue = this.model[prop]
      this.model[prop] = newValue
      RestApi({ ...this.saveEndpoint, ...{ data: this.model } })
        .then(() => {
          this.$message({
            message: this.$t('Profiles_Channels_UpdateSuccessMessage'),
            type: 'success'
          })
          this.$refs['marketing-rest-page'].loading = false
        })
        .catch(error => {
          this.model[prop] = oldValue
          this.$refs['marketing-rest-page'].loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    getMarketingStatusIcon(value) {
      if (value === subscriptionStatusKeys.Unsubscribed) {
        return 'el-icon-error'
      } else if (value === subscriptionStatusKeys.Subscribed) {
        return 'el-icon-success'
      } else if (value === subscriptionStatusKeys.Undefined) {
        return undefined
      }
    },

    getMargetingStatusLabel(value) {
      if (value === subscriptionStatusKeys.Unsubscribed) {
        return this.$t('Profiles_Channels_StatusUnsubscribed')
      } else if (value === subscriptionStatusKeys.Subscribed) {
        return this.$t('Profiles_Channels_StatusSubscribed')
      } else if (value === subscriptionStatusKeys.Undefined) {
        return this.$t('Profiles_Channels_StatusNotProvided')
      }
    },

    getChannelStatusLabel(status, value) {
      if (value === null || value === '') {
        return this.$t('Profiles_Channels_StatusNotProvided')
      } else if (status === 1) {
        return this.$t('Profiles_Channels_StatusAddressable')
      } else if (status === 0) {
        return this.$t('Profiles_Channels_StatusNotAddressable')
      }
    },

    getChannelStatusIcon(status, value) {
      if (value === null || value === '') {
        return undefined
      } else if (status === 1) {
        return 'el-icon-success'
      } else if (status === 0) {
        return 'el-icon-error'
      }
    },

    showSubscriptionLogs(channel) {
      this.$refs['subscription-logs'].show(channel)
    }
  }
}
</script>
<style scoped>
.el-icon-more {
  transform: rotate(90deg);
  font-size: 14px;
}
.addressability-container {
  margin-left: unset !important;
  margin-right: unset !important;
}

.addressability-left-section {
  padding-left: unset !important;
}

.addressability-right-section {
  padding-right: unset !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfileChannelsPage.vue